import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Icon,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import C from '@shared/constants/general';
import { LiveContainer } from '@/js/live/LiveContainer';
import FollowToggle from '@/js/shared/components/lists/FollowToggle';
import CoChipSmall from '@/js/shared/components/ui/CoChipSmall';
import { EntityCards } from '@/js/shared/components/ui/EntityCards';
import UserAvatar from '@/components/ui/userAvatar';
import { AuthContext, ConferenceContext, UserContext } from '@/store';
import { infoList } from '@/lib/utils/DataFormat';
import axios from 'axios';

const useStyles = (theme) => ({
  profileBg: {
    backgroundColor: `${theme.palette.background.default} !important`,
    minHeight: '100vh !important',
  },
  editButton: {
    color: 'rgba(0, 0, 0, 0.87) !important',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
    },
  },
  profileAvatarBg: {
    backgroundColor: `${theme.palette.background.shadedown_20} !important`,
  },
  confBackground: {
    width: '80px !important',
    height: '100px !important',
    backgroundSize: 'cover !important',
    backgroundPosition: 'center !important',
  },
  confEditor: {
    backgroundColor: '#222222 !important',
  },
  infoTitle: {
    textTransform: 'uppercase !important',
    opacity: '0.7 !important',
  },
  unselected: {
    opacity: '0.5 !important',
  },
  linkIcon: {
    marginRight: '8px !important',
  },
  noPadding: {
    padding: '0px !important',
  },
  infoDescription: {
    whiteSpace: 'pre-line !important',
  },
  rqimg: {
    width: '100% !important',
    maxWidth: '296px !important',
    height: 'auto !important',
    borderRadius: '5px !important',
    overflow: 'hidden !important',
  },
  table: {
    minWidth: '650px !important',
    border: '1px solid #e0e0e0 !important',
    backgroundColor: '#f9f9f9 !important',
  },
  paper: {
    padding: `${theme.spacing(2)} !important`,
    textAlign: 'left !important',
    background: `${theme.palette.background.paper} !important`,
    borderRadius: '10px !important',
    minHeight: '10px !important',
    maxHeight: '300px !important',
    height: '100% !important',
    overflow: 'auto !important',
  },
  question: {
    marginTop: '10px !important',
    width: '100% !important',
  },
});

const Profile = (props) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { userState, dispatch, actions } = useContext(UserContext); // Hämta Context
  const { conferenceState, actions: conferenceActions } = useContext(
    ConferenceContext
  ); // Hämta Context
  const { authState } = useContext(AuthContext); // Hämta Context
  const [pretixData, setPretixData] = useState({});
  const [pretixProductData, setPretixProductData] = useState(null);
  const [pretixTicketData, setPretixTicketData] = useState(null);
  const [pretixAddonData, setPretixAddonData] = useState(null);
  const [pretixProductError, setPretixProductError] = useState(null);
  const [pretixError, setPretixError] = useState('');
  const params = useParams();
  console.log('pretixProductData', pretixProductData);
  const isMe =
    props.isMe || (authState.userLoggedIn && authState.user.id === params.id);

  const updateUser = (userData) => {
    dispatch({ type: 'USER_SUCCESS', user: userData });
  };

  const toggleStatus = (status) => {
    actions.updateVisitorStatus(
      conferenceState.conferenceDetail.id,
      params.id,
      status
    );
  };

  const fetchPretixProductData = () => {
    axios
      .get(`/api/mypretixproductdata`)
      .then((response) => {
        const data = response.data;
        // A Pretix order can cover multiple attendees — only show tickets
        // belonging to the logged-in user, and only add-ons attached to
        // one of those tickets (not another attendee's).
        const ticketData = data.positions.filter(
          (position) =>
            position.addon_to === null &&
            position.attendee_email?.toLowerCase() ===
              authState.user.email?.toLowerCase()
        );
        const addonData = data.positions.filter(
          (position) =>
            position.addon_to !== null &&
            ticketData.some((ticket) => ticket.positionid === position.addon_to)
        );
        setPretixTicketData(ticketData);
        setPretixAddonData(addonData);
        setPretixProductData(data);
      })
      .catch((error) => {
        setPretixProductError(error.response.data.message);
      });
  };

  const fetchPretixData = () => {
    axios
      .get(`/api/mypretixdata`)
      .then((response) => {
        const data = response.data;
        setPretixData(data);
      })
      .catch((error) => {
        setPretixError(error.response.data.message);
      });
  };

  useEffect(() => {
    if (authState.userLoggedIn) {
      actions.loadPublicProfile(isMe ? authState.user.id : params.id);
      fetchPretixData();
      fetchPretixProductData();
    }
    conferenceActions.loadPrefetchedDetail();
  }, [authState.userLoggedIn]);

  return userState.userLoaded && userState.user ? (
    <LiveContainer welcomeState={props.welcomeState}>
      <Box sx={classes.profileBg}>
        <Box sx={classes.profileAvatarBg}>
          {/*<VisitorNav backArrow={'/'} fullscreen={true} {...props} />*/}
          <Container sx={classes.noPadding} maxWidth="lg">
            <Box align="center" pb={2} pt={2}>
              <UserAvatar size="large" user={userState.user} />
              <Box py={1}>
                <Typography variant="h5">{userState.user.full_name}</Typography>
                {userState.user.title && (
                  <Typography variant="body1" sx={classes.infoTitle}>
                    {userState.user.title}
                  </Typography>
                )}
                <Typography variant="body2">
                  {infoList([
                    userState.user.company,
                    userState.user.location_city,
                  ])}
                </Typography>
                {isMe && (
                  <Button
                    sx={classes.editButton}
                    href="/profile/edit"
                    startIcon={<Icon>edit</Icon>}
                  >
                    Edit profile
                  </Button>
                )}
              </Box>
            </Box>
          </Container>
        </Box>

        <Box align="center" pb={2} pt={2} ml={4} mr={4} mb={4} mt={2}>
          {pretixProductData && (
            <>
              <Grid
                container
                spacing={3}
                justifyContent="center"
                mb={{ xs: 0, md: 2, lg: 2 }}
              >
                <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                  <Typography variant="h6" sx={{ textAlign: 'left' }}>
                    {/* Min biljett for {pretixProductData?.event?.toUpperCase()} */}
                    Min biljett
                  </Typography>
                </Grid>
                <Grid item size={{ xs: 12, md: 6, lg: 4 }}></Grid>
              </Grid>
              <Grid container spacing={3} justifyContent="center">
                {/* {pretixProductData?.positions?.map((position) => ( */}
                <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                  <Paper sx={classes.paper}>
                    <Typography variant="h6" mb={2}>
                      {/* {position?.item?.name?.[pretixProductData?.locale]} -{' '}
                    {
                      position?.item?.category?.name?.[
                        pretixProductData?.locale
                      ]
                    } */}
                      {`Biljett / Ticket`}
                    </Typography>
                    {pretixTicketData &&
                      pretixTicketData.map((ticket) => (
                        <Box key={ticket.positionid}>
                          <Typography variant="body1" mb={1}>
                            - {ticket.item.name?.[pretixProductData?.locale]}
                          </Typography>
                        </Box>
                      ))}
                  </Paper>
                </Grid>
                <Grid item size={{ xs: 12, md: 6, lg: 4 }}>
                  <Paper sx={classes.paper}>
                    <Typography
                      variant="h6"
                      mb={2}
                    >{`Tillval / Add-ons`}</Typography>
                    {pretixAddonData &&
                      pretixAddonData.map((addon) => (
                        <Box key={addon.positionid}>
                          <Typography variant="body1" mb={1}>
                            - {addon.item.name?.[pretixProductData?.locale]}
                          </Typography>
                        </Box>
                      ))}
                  </Paper>
                </Grid>
                {/* ))} */}
              </Grid>
            </>
          )}
          {!pretixProductData && !pretixProductError && (
            <Box align="center">
              <CircularProgress disableShrink />
            </Box>
          )}
          {pretixData && pretixData.qr && (
            <Box textAlign="center" mt={2}>
              Check in:
              <br />
              <Box component="img" sx={classes.rqimg} src={pretixData.qr} />
            </Box>
          )}
          {/* {pretixError && (
            <Box
              textAlign="center"
              sx={{ fontSize: '18px' }}
              color="error.main"
            >
              {pretixError}
            </Box>
          )} */}
        </Box>
        <Box>
          <Container maxWidth="sm">
            <Paper>
              <Box py={2} align="center">
                {!isMe && (
                  <Box pt={1} pb={1}>
                    <FollowToggle
                      updatedCallback={updateUser}
                      user={userState.user}
                    />
                  </Box>
                )}

                {userState.user.tags
                  .filter((tag) => tag.type === 'skill')
                  .map((tag) => (
                    <CoChipSmall
                      key={tag.id}
                      tagtype={tag.type}
                      label={tag.title}
                    />
                  ))}
                {userState.user.tags
                  .filter((tag) => tag.type === 'scouting')
                  .map((tag) => (
                    <CoChipSmall
                      key={tag.id}
                      tagtype={tag.type}
                      label={tag.title}
                    />
                  ))}

                {userState.user.info_description ? (
                  <Box display="flex" align="left" pt={1} alignItems="center">
                    <Typography variant="body2" sx={classes.infoDescription}>
                      {userState.user.info_description}
                    </Typography>
                  </Box>
                ) : (
                  <></>
                )}

                {userState.user.info_website && (
                  <Box display="flex" pt={1} alignItems="center">
                    <Icon sx={classes.linkIcon}>home</Icon>{' '}
                    <Link
                      target="_blank"
                      href={`${userState.user.info_website}`}
                    >
                      {userState.user.info_website}
                    </Link>
                  </Box>
                )}

                {!isMe &&
                  authState.user.access_level ===
                    C.VISITOR.ACCESS_LEVEL.MODERATOR && (
                    <Box py={2} sx={{ fontSize: '18px' }}>
                      <em>
                        As you are moderator you can bann users, making them
                        unable to perform any actions like chatting etc.
                      </em>
                      <br />
                      <br />
                      {userState.user.status === C.USER.STATUS.BANNED && (
                        <>
                          <Box color="error.main">
                            {userState.user.full_name} is banned
                          </Box>
                          <Button
                            onClick={() => toggleStatus(C.USER.STATUS.VERIFIED)}
                            color="secondary"
                            variant="contained"
                            startIcon={<Icon>block</Icon>}
                          >
                            un:Bann user
                          </Button>
                        </>
                      )}
                      {userState.user.status === C.USER.STATUS.VERIFIED && (
                        <Button
                          onClick={() => toggleStatus(C.USER.STATUS.BANNED)}
                          color="primary"
                          variant="contained"
                          startIcon={<Icon>block</Icon>}
                        >
                          Bann user
                        </Button>
                      )}
                    </Box>
                  )}
              </Box>
            </Paper>
          </Container>
          {userState.user.entities.length > 0 && (
            <Container maxWidth="lg">
              <EntityCards entities={userState.user.entities} history={null} />
            </Container>
          )}
        </Box>
      </Box>
    </LiveContainer>
  ) : (
    <Box align="center">
      <CircularProgress disableShrink />
    </Box>
  );
};

export default Profile;
